/**
 * Papelera para los adjuntos del cajón de un documento: contabilización,
 * XML y vinculado.
 *
 * Por qué existe (2026-09-23): esas tres acciones hacían borrado duro — el
 * doc `files/{…}` desaparecía y la referencia salía del padre — y nunca
 * pasaban por la papelera. El 2026-09-22 se reversaron por error dos
 * contabilizaciones en producción y solo se pudieron recuperar a mano desde
 * el audit. Ahora cada borrado deja su entrada en `trash` y se restaura desde
 * la pantalla.
 *
 * Este módulo decide QUÉ se guarda y QUÉ se escribe al restaurar; las
 * escrituras las hacen los thunks (actions/database/documents.js al borrar,
 * actions/database/trash.js al restaurar). Módulo puro: sin Firestore, para
 * poder testearlo directo. Las referencias se reconocen por su `.path`.
 *
 * Storage nunca se toca, ni al borrar ni al vaciar la papelera: el PDF sigue
 * donde estaba, como con el resto de la papelera.
 */

import { toAccountingList } from './accounting_refs';

/** Tipos de adjunto y el campo del documento padre donde vive su referencia. */
export const ATTACHMENT_KINDS = {
  accounting: { itemType: 'accounting', field: 'accounting', label: 'Contabilización' },
  xml:        { itemType: 'xml',        field: 'xml',        label: 'XML' },
  link:       { itemType: 'link',       field: 'links',      label: 'Vinculado' },
};

const RETENTION_DAYS = 30;

const extOf = (name) => {
  const s = String(name || '');
  const i = s.lastIndexOf('.');
  return i > 0 ? s.slice(i + 1).toLowerCase() : null;
};

/**
 * Datos de la entrada de papelera para un adjunto, SIN `deletedAt` (el thunk
 * le pone el serverTimestamp). Va en el mismo batch que el borrado: o se
 * borra y queda en la papelera, o no pasa nada.
 *
 * @param {object} p
 * @param {'accounting'|'xml'|'link'} p.kind
 * @param {string} p.attachmentPath  ruta del doc del adjunto (files/…)
 * @param {object} p.attachmentData  datos del doc tal cual, para recrearlo
 * @param {string} p.parentPath      ruta del documento al que pertenece
 * @param {string} [p.parentName]    nombre del documento, para la ubicación
 * @param {string} [p.customerId]    extractCustomerId de la carpeta del padre
 * @param {string} p.uid             quién borra (la papelera filtra por esto)
 * @param {string} [p.userName]
 * @param {{uid:string, name?:string}} [p.requestedBy]  solo si el borrado sale de
 *   una solicitud aprobada: quién la pidió (para que lo vea en su papelera)
 */
export const buildAttachmentTrashEntry = ({
  kind,
  attachmentPath,
  attachmentData,
  parentPath,
  parentName,
  customerId,
  uid,
  userName,
  requestedBy,
}) => {
  const meta = ATTACHMENT_KINDS[kind];
  if (!meta) throw new Error(`Tipo de adjunto desconocido: ${kind}`);
  const name = attachmentData?.name || '';
  return {
    customerId: customerId || null,
    itemType: meta.itemType,
    itemName: name,
    itemExt: extOf(name),
    breadcrumb: parentName ? [parentName] : [],
    deletedBy: uid,
    deletedByName: userName || null,
    restorePayload: {
      kind,
      attachmentPath,
      attachmentData: attachmentData || {},
      parentPath,
    },
    retentionDays: RETENTION_DAYS,
    ...(requestedBy?.uid ? { requestedBy: requestedBy.uid, requestedByName: requestedBy.name || null } : {}),
  };
};

const withoutPath = (list, path) => (list || []).filter((r) => r?.path !== path);

/**
 * Qué escribir en el documento padre al restaurar. Se calcula sobre el
 * estado ACTUAL del padre (releído al restaurar), no sobre la foto del
 * borrado: entre medio pudo cambiar.
 *
 * @param {object}   p
 * @param {object}   p.restorePayload
 * @param {{exists:boolean, data:object}} p.parent
 * @param {function} p.makeRef  path → DocumentReference
 * @returns {{error:string} | {field:string, next:any, denormalize:boolean}}
 */
export const planAttachmentRestore = ({ restorePayload, parent, makeRef }) => {
  const { kind, attachmentPath, parentPath } = restorePayload || {};
  const meta = ATTACHMENT_KINDS[kind];
  if (!meta || !attachmentPath || !parentPath) {
    return { error: 'El registro de la papelera está incompleto; no se puede restaurar.' };
  }
  if (!parent?.exists) {
    return { error: 'El documento al que pertenecía ya no existe; no se puede restaurar.' };
  }
  const data = parent.data || {};
  const restored = makeRef(attachmentPath);

  if (kind === 'accounting') {
    // Una sola contabilización por documento (accounting_refs.js): si ya
    // tiene otra, restaurar la dejaría con dos. Igual que con el XML.
    const current = withoutPath(toAccountingList(data.accounting), attachmentPath);
    if (current.length > 0) {
      return { error: 'Este documento ya tiene una contabilización. Elimínala primero si quieres recuperar la anterior.' };
    }
    // Lista nueva y no arrayUnion: sobre una referencia suelta (formato
    // viejo) arrayUnion la REEMPLAZA.
    return { field: meta.field, next: [restored], denormalize: true };
  }

  if (kind === 'xml') {
    // Un documento tiene un solo XML. Pisar uno subido después del borrado
    // sería otro borrado sin papelera.
    if (data.xml?.path && data.xml.path !== attachmentPath) {
      return { error: 'Este documento ya tiene un XML. Elimínalo primero si quieres recuperar el anterior.' };
    }
    return { field: meta.field, next: restored, denormalize: false };
  }

  // Vinculado
  const current = withoutPath(Array.isArray(data.links) ? data.links : [], attachmentPath);
  return { field: meta.field, next: [...current, restored], denormalize: false };
};
